import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Hash } from 'lucide-react';

import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterFlash }        from '@/components/master/MasterFlash';
import { MasterAuditTab }     from '@/components/master/MasterAuditTab';
import { MasterToolbar }          from '@/components/master/MasterToolbar';
import { MasterSearchModal }      from '@/components/master/MasterSearchModal';
import type { SearchColumn }      from '@/components/master/MasterSearchModal';
import { MasterReportPreview }    from '@/components/master/report/MasterReportPreview';
import { ReportHeader }           from '@/components/master/report/ReportHeader';
import { ReportSection }          from '@/components/master/report/ReportSection';
import { ReportFooter }           from '@/components/master/report/ReportFooter';
import { ConfirmDialog }          from '@/components/ui/ConfirmDialog';
import { Input }                  from '@/components/ui/Input';
import { Badge }                  from '@/components/ui/Badge';
import { Tabs } from '@/components/ui/Tabs';
import { Toggle } from '@/components/ui/Toggle';
import { Combobox } from '@/components/ui/Combobox';
import { Field } from '@/components/ui/Field';
import { Card }                   from '@/components/ui/Card';

import { useAuth }                from '@/hooks/useAuth';
import { usePermission }          from '@/hooks/usePermission';
import { useFlashMessage }    from '@/hooks/useFlashMessage';
import { useMasterScreenState }   from '@/hooks/useMasterScreenState';
import { Permissions }            from '@/constants/permissions';
import { AppRoutes }              from '@/constants/routes';
import { documentNumberingApi }   from '@/api/documentNumbering.api';
import type {
  DocumentNumberingDto, DocumentNumberingListDto,
} from '@/types/documentNumbering.types';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  transactionTypeCode: z.string().min(1, 'Transaction type code is required').max(20),
  prefix:              z.string().max(20).nullable().optional(),
  suffix:              z.string().max(20).nullable().optional(),
  paddingLength:       z.coerce.number().int().min(1).max(20),
  isActive:            z.boolean(),
  resetRule:           z.enum(['NEVER', 'YEARLY', 'MONTHLY']),
  isYearBased:         z.boolean(),
  isLocationBased:     z.boolean(),
  autoGenerate:        z.boolean(),
  allowManualOverride: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  transactionTypeCode: '', prefix: null, suffix: null, paddingLength: 6,
  isActive: true, resetRule: 'NEVER', isYearBased: false, isLocationBased: false,
  autoGenerate: true, allowManualOverride: false,
};

const TABS = ['General', 'Sequence Configuration', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<DocumentNumberingListDto>[] = [
  { key: 'transactionTypeCode', label: 'Transaction Type', className: 'w-36 font-mono' },
  { key: 'prefix',              label: 'Prefix',   className: 'w-20 font-mono', render: r => r.prefix ?? '—' },
  { key: 'suffix',              label: 'Suffix',   className: 'w-20 font-mono', render: r => r.suffix ?? '—' },
  { key: 'currentNumber',       label: 'Current #', className: 'w-20 text-right font-mono' },
  { key: 'resetRule',           label: 'Reset',    className: 'w-20' },
  { key: 'isActive',            label: 'Status',   className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: DocumentNumberingDto): FormValues {
  return {
    transactionTypeCode: dto.transactionTypeCode,
    prefix: dto.prefix ?? null, suffix: dto.suffix ?? null,
    paddingLength: dto.paddingLength, isActive: dto.isActive,
    resetRule: dto.resetRule as 'NEVER' | 'YEARLY' | 'MONTHLY',
    isYearBased: dto.isYearBased, isLocationBased: dto.isLocationBased,
    autoGenerate: dto.autoGenerate, allowManualOverride: dto.allowManualOverride,
  };
}

/** Build the sample format preview string */
function buildSample(prefix: string | null | undefined, currentNumber: number, padding: number, suffix: string | null | undefined): string {
  const num = String(currentNumber ?? 1).padStart(padding, '0');
  const parts = [prefix?.trim(), num, suffix?.trim()].filter(Boolean);
  return parts.join('-');
}

/** Binds a boolean to the shared Toggle. Kept local only to name the props this page uses. */
const ToggleSwitch = ({
  label, description, checked, onChange, disabled,
}: {
  label: string; description?: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) => (
  <Toggle label={label} description={description}
    checked={checked} onChange={onChange} disabled={disabled} />
);

// ── Component ─────────────────────────────────────────────────────────────────

export const DocumentNumberingPage = () => {
  const navigate = useNavigate();
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.CFG_DOC_NUMBERING_VIEW);
  const canCreate = hasPermission(Permissions.CFG_DOC_NUMBERING_CREATE);
  const canEdit   = hasPermission(Permissions.CFG_DOC_NUMBERING_EDIT);
  const canDelete = hasPermission(Permissions.CFG_DOC_NUMBERING_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [record,       setRecord]       = useState<DocumentNumberingDto | null>(null);
  const [codeInput,    setCodeInput]    = useState('');
  const [activeTab,    setActiveTab]    = useState<Tab>('General');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lookupBusy,   setLookupBusy]   = useState(false);
  const [printOpen,    setPrintOpen]    = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const watchedPrefix  = watch('prefix');
  const watchedSuffix  = watch('suffix');
  const watchedPadding = watch('paddingLength');
  const isActive       = watch('isActive');

  const resetToInitial = useCallback(() => {
    setRecord(null); setCodeInput(''); reset(EMPTY_FORM);
    screen.setMode('initial'); setActiveTab('General'); setPendingSave(null);
    setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: DocumentNumberingDto) => {
    setRecord(dto); reset(dtoToForm(dto)); setCodeInput(dto.transactionTypeCode);
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view document numbering.'); return; }
    setLookupBusy(true);
    try {
      const res = await documentNumberingApi.getByTransactionType(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `No numbering configuration found for '${code}'.`); return; }
        setShowNewConfirm(code);
      } else { flash('error', 'Lookup failed. Please try again.'); }
    } finally { setLookupBusy(false); }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm; setShowNewConfirm(null); if (!code) return;
    reset({ ...EMPTY_FORM, transactionTypeCode: code }); setCodeInput(code);
    setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleRefresh   = () => resetToInitial();
  const handleEdit      = () => screen.setMode('edit');
  const handleSaveClick = () => { handleSubmit(v => { setPendingSave(v); setShowSaveConfirm(true); })(); };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await documentNumberingApi.create({
          transactionTypeCode: d.transactionTypeCode.trim().toUpperCase(),
          prefix: n(d.prefix), suffix: n(d.suffix), paddingLength: d.paddingLength,
          resetRule: d.resetRule, isYearBased: d.isYearBased, isLocationBased: d.isLocationBased,
          autoGenerate: d.autoGenerate, allowManualOverride: d.allowManualOverride,
        });
        flash('success', 'Document numbering configuration created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await documentNumberingApi.update(record.id, {
          prefix: n(d.prefix), suffix: n(d.suffix), paddingLength: d.paddingLength,
          resetRule: d.resetRule, isYearBased: d.isYearBased, isLocationBased: d.isLocationBased,
          autoGenerate: d.autoGenerate, allowManualOverride: d.allowManualOverride, isActive: d.isActive,
        });
        flash('success', 'Document numbering configuration updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false); setSaving(true);
    try {
      await documentNumberingApi.remove(record.id);
      flash('success', `Numbering config for '${record.transactionTypeCode}' deleted.`); resetToInitial();
    } catch { flash('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const handlePrint = () => { if (record) setPrintOpen(true); };
  const handleExit  = () => navigate(AppRoutes.DASHBOARD);

  const fetchConfigs = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await documentNumberingApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  const handleSearchSelect = (item: DocumentNumberingListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    documentNumberingApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load configuration.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;
  const sampleNumber   = buildSample(watchedPrefix, record?.currentNumber ?? 1, watchedPadding ?? 6, watchedSuffix);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={Hash} title="Document Numbering Setup"
        subtitle="Configure automatic document numbering sequences" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} saving={saving} loading={lookupBusy}
        onSave={handleSaveClick} onEdit={handleEdit} onPrint={handlePrint}
        onDelete={handleDelete} onRefresh={handleRefresh} onExit={handleExit}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Transaction Type"
        summary={record && (<>
            <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-700 dark:text-gray-300">
              {sampleNumber}
            </span>
            <span className="text-xs text-gray-400">current #: {record.currentNumber}</span>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
            )}
        </>)}
        hint={screen.isInitial ? "Enter a transaction type code and press Enter, or browse." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search sequences"
          placeholder="Type code + Enter…"
          maxLength={20}
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Numbering Configuration</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Transaction Type Code" {...register('transactionTypeCode')} disabled
                    placeholder="— enter via lookup bar above —" className="font-mono uppercase sm:col-span-2" />

                  <div>
                    <Input label="Prefix" {...register('prefix')} disabled={fieldsDisabled} maxLength={20} placeholder="e.g. GL-JV" className="font-mono" />
                  </div>

                  <div>
                    <Input label="Suffix" {...register('suffix')} disabled={fieldsDisabled} maxLength={20} placeholder="e.g. -A" className="font-mono" />
                  </div>

                  <div>
                    <Input label="Padding Length" type="number" {...register('paddingLength')} disabled={fieldsDisabled} min={1} max={20} className="font-mono" />
                    {errors.paddingLength && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.paddingLength.message}</p>}
                  </div>

                  {(screen.isEdit || screen.isView) && (
                    <div>
                      <Controller name="isActive" control={control} render={({ field }) => (
                        <ToggleSwitch label={isActive ? 'Active' : 'Inactive'} checked={field.value}
                          onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                    </div>
                  )}

                  {/* Sample format preview */}
                  <div className="sm:col-span-2">
                    <Field label="Sample Format Preview">
                      <div className="flex items-center h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                        <span className="font-mono text-sm font-bold text-brand-600 dark:text-brand-400">{sampleNumber}</span>
                        <span className="ml-3 text-xs text-gray-400">(based on current number: {record?.currentNumber ?? 1})</span>
                      </div>
                    </Field>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'Sequence Configuration' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Sequence Settings</h3>

                <div className="mb-4">
                  <Controller name="resetRule" control={control} render={({ field }) => (
                    <Combobox label="Reset Rule" clearable={false}
                      options={[{ value: "NEVER", label: "NEVER — Never reset" }, { value: "YEARLY", label: "YEARLY — Reset each year" }, { value: "MONTHLY", label: "MONTHLY — Reset each month" }]}
                      value={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                  )} />
                </div>

                <div className="space-y-0">
                  {([
                    ['isYearBased',         'Year Based',             'Number sequence resets / restarts based on year.'],
                    ['isLocationBased',     'Location Based',         'Each location maintains its own independent sequence.'],
                    ['autoGenerate',        'Auto Generate',          'Document number is automatically assigned on creation.'],
                    ['allowManualOverride', 'Allow Manual Override',  'Users can manually enter document numbers instead of using auto-generated ones.'],
                  ] as [keyof FormValues, string, string][]).map(([fieldName, label, desc]) => (
                    <Controller key={fieldName} name={fieldName} control={control} render={({ field }) => (
                      <ToggleSwitch label={label} description={desc}
                        checked={field.value as boolean} onChange={field.onChange} disabled={fieldsDisabled} />
                    )} />
                  ))}
                </div>

                {record && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <Field label="Current Number">
                      <div className="flex items-center h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 w-36">
                        <span className="font-mono text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {record.currentNumber}
                        </span>
                      </div>
                    </Field>
                    <p className="mt-1 text-[10px] text-gray-400">
                      Current number is system-managed. It increments automatically with each document generated.
                    </p>
                  </div>
                )}
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      <MasterSearchModal<DocumentNumberingListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Document Numbering Configurations" columns={SEARCH_COLUMNS}
        fetchData={fetchConfigs} onSelect={handleSearchSelect}
      />

      <ConfirmDialog isOpen={showNewConfirm !== null} variant="info"
        title="Create New Document Numbering Configuration"
        message={<span>No numbering configuration exists for transaction type <strong className="font-mono">{showNewConfirm}</strong>.<br />Create a new configuration?</span>}
        confirmLabel="Yes, Create" cancelLabel="No"
        onConfirm={handleConfirmCreateNew} onCancel={() => setShowNewConfirm(null)}
      />
      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={screen.isNew ? 'Save Numbering Configuration' : 'Save Changes'}
        message={screen.isNew
          ? 'Save this numbering configuration?'
          : `Save changes to numbering config for "${record?.transactionTypeCode}"?`}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />
      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Numbering Configuration"
        message={<span>Delete numbering configuration for <strong>{record?.transactionTypeCode}</strong>?<br /><span className="text-xs text-gray-400">Soft-delete only. Auto-numbering will stop for this transaction type.</span></span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Document Numbering Report">
          <ReportHeader reportTitle="Document Numbering Configuration Report"
            printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
            meta={[{ label: 'Transaction Type', value: record.transactionTypeCode }]}
          />
          <ReportSection title="Configuration" fields={[
            { label: 'Transaction Type', value: record.transactionTypeCode },
            { label: 'Prefix',           value: record.prefix },
            { label: 'Suffix',           value: record.suffix },
            { label: 'Padding Length',   value: record.paddingLength },
            { label: 'Current Number',   value: record.currentNumber },
            { label: 'Sample Format',    value: buildSample(record.prefix, record.currentNumber, record.paddingLength, record.suffix), wide: true },
            { label: 'Status',           value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
          ]} />
          <ReportSection title="Sequence Rules" fields={[
            { label: 'Reset Rule',          value: record.resetRule },
            { label: 'Year Based',          value: record.isYearBased, display: record.isYearBased ? 'Yes' : 'No' },
            { label: 'Location Based',      value: record.isLocationBased, display: record.isLocationBased ? 'Yes' : 'No' },
            { label: 'Auto Generate',       value: record.autoGenerate, display: record.autoGenerate ? 'Yes' : 'No' },
            { label: 'Manual Override',     value: record.allowManualOverride, display: record.allowManualOverride ? 'Allowed' : 'Not Allowed' },
          ]} />
          <ReportSection title="Audit Information" fields={[
            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
            { label: 'Created Date',  value: formatDateTime(record.createdDate) },
            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
            { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
